import { createClient } from '@supabase/supabase-js';
import { createGuardedSupabaseFetch } from '../auth/guardedSupabaseFetch';
import { getAuthTransportExpectation } from '../auth/sessionSignals';

const isTest = process.env.NODE_ENV === 'test';

const envUrl =
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  process.env.VITE_SUPABASE_URL ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_URL) ||
  (isTest ? 'http://localhost' : undefined);

const envAnon =
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  process.env.VITE_SUPABASE_ANON_KEY ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_ANON_KEY) ||
  (isTest ? 'test-anon-key' : undefined);

function resolveAuthStorageKey(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const projectRef = new URL(url).hostname.split('.')[0];
    return projectRef ? `sb-${projectRef}-auth-token` : null;
  } catch {
    return null;
  }
}

export const SUPABASE_AUTH_STORAGE_KEY = resolveAuthStorageKey(envUrl);
const PUBLIC_SUPABASE_AUTH_STORAGE_KEY = `${SUPABASE_AUTH_STORAGE_KEY ?? 'supabase-auth'}-public`;

// Fail fast if misconfigured (helps preview diagnostics)
if (!envUrl || !envAnon) {
  if (!isTest) {
    // eslint-disable-next-line no-console
    console.error('Supabase ENV missing: NEXT_PUBLIC_SUPABASE_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY');
  }
}

/**
 * Resilient lock that uses navigator.locks when available but falls back
 * gracefully when the Web Locks API malfunctions (AbortError thrown by
 * certain browser environments, corporate proxies, or extensions).
 *
 * Single-tab correctness is preserved because GoTrueClient serialises
 * operations via an internal pendingInLock queue.  The navigator lock only
 * coordinates ACROSS tabs, so losing it means concurrent tabs may race on
 * token refresh — but the Supabase server handles that via its refresh-
 * token reuse interval.
 */
async function resilientLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  if (
    typeof globalThis.navigator === 'undefined' ||
    !globalThis.navigator?.locks
  ) {
    return await fn();
  }

  try {
    const ac = new AbortController();

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (acquireTimeout > 0) {
      timer = setTimeout(() => ac.abort(), acquireTimeout);
    }

    const result = await globalThis.navigator.locks.request(
      name,
      acquireTimeout === 0
        ? { mode: 'exclusive' as const, ifAvailable: true }
        : { mode: 'exclusive' as const, signal: ac.signal },
      async (lock) => {
        if (lock) {
          return await fn();
        }
        // Lock not acquired (ifAvailable path or non-spec browser) — run anyway
        return await fn();
      },
    );

    if (timer !== undefined) clearTimeout(timer);
    return result;
  } catch (err: unknown) {
    // Re-throw Supabase's own LockAcquireTimeoutError so its retry logic
    // can handle it as expected.
    if (
      err &&
      typeof err === 'object' &&
      'isAcquireTimeout' in err &&
      (err as { isAcquireTimeout: boolean }).isAcquireTimeout
    ) {
      throw err;
    }

    // For AbortError or any other lock failure, fall back to running
    // without cross-tab locking so the user can still authenticate.
    // eslint-disable-next-line no-console
    console.warn(
      '[supabase-lock] navigator.locks.request() failed, running without cross-tab lock:',
      err instanceof Error ? `${err.name}: ${err.message}` : String(err),
    );
    return await fn();
  }
}

export const guardedSupabaseFetch = createGuardedSupabaseFetch(envAnon);

/**
 * Calls a Data API function with fetch keepalive, built and sent before this returns, so the browser
 * delivers it even while the page unloads (an awaited supabase-js call is cancelled with the page).
 * It uses the signed-in session's current access token, through the guarded transport. Returns null
 * when there is no staff session to send it with.
 */
export function sendRpcWithKeepalive(functionName: string, args: Record<string, unknown>): Promise<Response> | null {
  const { mode, accessToken } = getAuthTransportExpectation();
  if (!envUrl || !envAnon || mode !== 'session' || !accessToken) return null;
  return guardedSupabaseFetch(`${envUrl.replace(/\/+$/, '')}/rest/v1/rpc/${functionName}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      apikey: envAnon,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  });
}

export const supabase = createClient(envUrl ?? '', envAnon ?? '', {
  global: { fetch: guardedSupabaseFetch },
  realtime: { params: { eventsPerSecond: 5 } },
  auth: {
    lock: resilientLock,
    ...(SUPABASE_AUTH_STORAGE_KEY ? { storageKey: SUPABASE_AUTH_STORAGE_KEY } : {}),
  },
});

/**
 * Capability-link transport for routes that intentionally execute only the
 * narrow anon RPC contract. It never restores or persists a staff session,
 * even when the link is opened in a browser that is already signed in.
 */
export const publicSupabase = createClient(envUrl ?? '', envAnon ?? '', {
  global: { fetch: guardedSupabaseFetch },
  auth: {
    storageKey: PUBLIC_SUPABASE_AUTH_STORAGE_KEY,
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

/** Guest credentials and MFA remain in memory until the complete sign-in is promoted. */
export function createGuestAuthClient() {
  return createClient(envUrl ?? '', envAnon ?? '', {
    global: { fetch: guardedSupabaseFetch },
    auth: { storageKey: 'portal-guest-signin', persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}
export default supabase;
